<script setup lang="ts">
  import { computed } from 'vue';
  import { Checkbox } from '@/shared/ui';
  import { DocumentRevisionStatus, DocumentRevisionStatusLabels } from '@/shared/lib';
  import { getDocumentRevisionStatusClasses } from '@/shared/lib/utils';
  import RecordListActionsCell from './RecordListActionsCell.vue';
  import type { RecordListRow, RecordRevisionListRow } from '../model/records.types';
  import type { RecordModalTarget } from '@/widgets/record-modals';
  import type { RecordDeleteSelection } from '../lib/record-delete-confirmation';

  /**
   * One row of the record list. With `revision` set the row renders a previous
   * revision of the same record: the design keeps every column and only changes
   * the background, the revision data and the set of actions.
   */
  const props = withDefaults(
    defineProps<{
      record: RecordListRow;
      revision?: RecordRevisionListRow | null;
      selected?: boolean;
      expanded?: boolean;
      openMenuKey?: string | null;
      /** Only a SUPERUSER sees the "Inativar" switch. Decided by the page. */
      showInactivateToggle?: boolean;
      /** A status write of this row is in flight, so the switch does not take a second click. */
      togglePending?: boolean;
    }>(),
    {
      revision: null,
      selected: false,
      expanded: false,
      openMenuKey: null,
      showInactivateToggle: false,
      togglePending: false,
    }
  );

  const emit = defineEmits<{
    'toggle-selection': [];
    'toggle-expand': [];
    'update:open-menu-key': [key: string | null];
    'open-history': [target: RecordModalTarget];
    'open-control': [target: RecordModalTarget];
    edit: [target: RecordModalTarget];
    configure: [target: RecordModalTarget];
    'edit-homologation': [target: RecordModalTarget];
    delete: [selection: RecordDeleteSelection];
    'toggle-active': [isActive: boolean];
  }>();

  const isRevisionRow = computed(() => props.revision !== null);

  /**
   * A record id and a revision id come from different tables, so they can be the
   * same number: the prefix keeps the ids of the checkbox unique in the document.
   */
  const checkboxId = computed(() =>
    props.revision ? `revision-${props.revision.id}` : props.record.id
  );

  /**
   * The title belongs to the revision, not to the code: a revision can be renamed,
   * so a revision row shows the title that revision carried and never the one of
   * the revision in force.
   *
   * A revision row keeps its own null, which renders the placeholder. Falling back
   * to the record here would put the current name on a revision that has none.
   */
  const rowTitle = computed(() => (props.revision ? props.revision.title : props.record.title));

  const title = computed(() => rowTitle.value ?? '-');

  const indexing = computed(
    () => (props.revision ? props.revision.indexing : props.record.indexing) ?? '-'
  );

  const revisionNumber = computed(() => props.revision?.revision ?? props.record.revision);

  /**
   * The inactivated record takes precedence over the status of its current
   * revision. A previous revision keeps its own status, because being obsolete is
   * a fact of that revision and not of the current state of the record.
   */
  const badgeStatus = computed(() => {
    if (props.revision) {
      return props.revision.revisionStatus;
    }

    return props.record.isActive ? props.record.revisionStatus : DocumentRevisionStatus.INACTIVE;
  });

  const badgeLabel = computed(() => DocumentRevisionStatusLabels[badgeStatus.value]);

  const badgeClass = computed(() => getDocumentRevisionStatusClasses(badgeStatus.value));

  const generatedRecordsCount = computed(
    () => props.revision?.generatedRecordsCount ?? props.record.generatedRecordsCount
  );

  const rowTestId = computed(() =>
    props.revision ? `record-revision-row-${props.revision.id}` : `record-row-${props.record.id}`
  );

  /**
   * Only one row menu stays open at a time, and the owner of that state knows
   * the rows by key. A record id and a revision id can be the same number, so
   * the key carries the kind of the row as well.
   */
  const menuKey = computed(() =>
    props.revision ? `revision-${props.revision.id}` : `record-${props.record.id}`
  );

  const menuOpen = computed(() => props.openMenuKey === menuKey.value);

  const handleMenuOpen = (open: boolean) => {
    emit('update:open-menu-key', open ? menuKey.value : null);
  };

  /**
   * Every modal of the row opens on the same subject: the record of the row and
   * the revision the row itself shows, so a revision row never hands over the
   * revision of the record.
   */
  const modalTarget = computed<RecordModalTarget>(() => ({
    id: props.revision ? props.revision.id : props.record.id,
    code: props.record.code,
    title: rowTitle.value,
    revision: revisionNumber.value,
  }));

  const handleDelete = () => {
    emit(
      'delete',
      props.revision
        ? { recordIds: [], revisionIds: [props.revision.id] }
        : { recordIds: [props.record.id], revisionIds: [] }
    );
  };
</script>

<template>
  <tr
    class="table-row-hover border-t border-gray-02 transition-colors duration-300 ease-out"
    :class="{ 'bg-gray-02/30': isRevisionRow }"
    :data-testid="rowTestId"
  >
    <!-- Selection -->
    <td class="pl-4 pr-2 py-4.5 align-middle leading-[0]">
      <Checkbox
        :id="checkboxId"
        :checked="props.selected"
        @toggle="emit('toggle-selection')"
      />
    </td>
    <!-- Record -->
    <td class="py-4.5 text-sm text-gray-07 text-left align-middle whitespace-nowrap">
      {{ props.record.code }}
    </td>
    <!-- Title -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left align-middle min-w-88 w-full">
      {{ title }}
    </td>
    <!-- Indexer -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left align-middle whitespace-nowrap">
      {{ indexing }}
    </td>
    <!-- Revision -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left align-middle whitespace-nowrap">
      {{ revisionNumber }}
    </td>
    <!-- Status -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left align-middle">
      <div class="flex items-center w-19">
        <span
          :class="badgeClass"
          class="text-center"
          data-testid="record-status-badge"
        >
          {{ badgeLabel }}
        </span>
      </div>
    </td>
    <!-- Generated records -->
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left align-middle whitespace-nowrap">
      {{ generatedRecordsCount }}
    </td>
    <!-- Actions -->
    <RecordListActionsCell
      :record="props.record"
      :is-revision-row="isRevisionRow"
      :expanded="props.expanded"
      :menu-open="menuOpen"
      :show-inactivate-toggle="props.showInactivateToggle"
      :toggle-pending="props.togglePending"
      :has-older-revisions="props.record.hasOlderRevisions"
      @toggle-expand="emit('toggle-expand')"
      @update:menu-open="handleMenuOpen"
      @open-history="emit('open-history', modalTarget)"
      @open-control="emit('open-control', modalTarget)"
      @edit="emit('edit', modalTarget)"
      @configure="emit('configure', modalTarget)"
      @edit-homologation="emit('edit-homologation', modalTarget)"
      @delete="handleDelete"
      @toggle-active="emit('toggle-active', $event)"
    />
  </tr>
</template>
